import {
  currentUpdateSchema,
  myUpdateViewSchema,
  periodDetailSchema,
  periodDigestSchema,
  periodPageSchema,
  reportingSettingsSchema,
  weeklyUpdateDetailSchema,
  weeklyUpdateRevisionSchema,
  type ExcuseReporterRequest,
  type SaveWeeklyUpdateRequest,
  type UpdateReportingSettingsRequest,
} from '@mfos/shared';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { apiGet, apiPost, apiPut, withQuery } from '../../lib/api-client';
import { workspaceKey, workspacePath } from '../workspace/workspace.api';
import { useWorkspaceMutation } from '../workspace/workspace-mutation';

const PERIOD_PAGE_SIZE = 12;

export const updatesKey = (workspaceId: string) =>
  [...workspaceKey(workspaceId), 'updates'] as const;
export const reportingSettingsKey = (workspaceId: string) =>
  [...workspaceKey(workspaceId), 'settings', 'reporting'] as const;

export function useCurrentUpdate(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: [...updatesKey(workspaceId), 'current'] as const,
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'updates', 'current'), {
        schema: currentUpdateSchema,
        signal,
      }),
    enabled,
  });
}

export function useMyUpdate(workspaceId: string, periodId: string, enabled = true) {
  return useQuery({
    queryKey: [...updatesKey(workspaceId), 'mine', periodId] as const,
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'updates', 'periods', periodId, 'mine'), {
        schema: myUpdateViewSchema,
        signal,
      }),
    enabled,
  });
}

export function usePeriods(workspaceId: string, enabled = true) {
  return useInfiniteQuery({
    queryKey: [...updatesKey(workspaceId), 'periods'] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiGet(
        withQuery(workspacePath(workspaceId, 'updates', 'periods'), {
          cursor: pageParam,
          limit: PERIOD_PAGE_SIZE,
        }),
        { schema: periodPageSchema, signal },
      ),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled,
  });
}

export function usePeriod(workspaceId: string, periodId: string) {
  return useQuery({
    queryKey: [...updatesKey(workspaceId), 'period', periodId] as const,
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'updates', 'periods', periodId), {
        schema: periodDetailSchema,
        signal,
      }),
  });
}

export function usePeriodDigest(workspaceId: string, periodId: string) {
  return useQuery({
    queryKey: [...updatesKey(workspaceId), 'digest', periodId] as const,
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'updates', 'periods', periodId, 'digest'), {
        schema: periodDigestSchema,
        signal,
      }),
  });
}

export function useWeeklyUpdate(workspaceId: string, updateId: string) {
  return useQuery({
    queryKey: [...updatesKey(workspaceId), 'update', updateId] as const,
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'updates', updateId), {
        schema: weeklyUpdateDetailSchema,
        signal,
      }),
  });
}

export function useUpdateRevision(workspaceId: string, updateId: string, revision: number | null) {
  return useQuery({
    queryKey: [...updatesKey(workspaceId), 'update', updateId, 'revision', revision] as const,
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'updates', updateId, 'revisions', String(revision ?? 0)), {
        schema: weeklyUpdateRevisionSchema,
        signal,
      }),
    enabled: revision !== null,
  });
}

export function useSaveUpdateDraft(workspaceId: string, periodId: string) {
  return useWorkspaceMutation(workspaceId, (body: SaveWeeklyUpdateRequest) =>
    apiPut(
      workspacePath(workspaceId, 'updates', 'periods', periodId, 'mine'),
      body,
      myUpdateViewSchema,
    ),
  );
}

export function useSubmitUpdate(workspaceId: string, periodId: string) {
  return useWorkspaceMutation(workspaceId, (body: SaveWeeklyUpdateRequest) =>
    apiPost(
      workspacePath(workspaceId, 'updates', 'periods', periodId, 'mine', 'submit'),
      body,
      myUpdateViewSchema,
    ),
  );
}

export function useExcuseReporter(workspaceId: string, periodId: string) {
  return useWorkspaceMutation(
    workspaceId,
    ({ memberId, body }: { memberId: string; body: ExcuseReporterRequest }) =>
      apiPost(
        workspacePath(workspaceId, 'updates', 'periods', periodId, 'reporters', memberId, 'excuse'),
        body,
        periodDetailSchema,
      ),
  );
}

export function useReportingSettings(workspaceId: string, enabled = true) {
  return useQuery({
    queryKey: reportingSettingsKey(workspaceId),
    queryFn: ({ signal }) =>
      apiGet(workspacePath(workspaceId, 'settings', 'reporting'), {
        schema: reportingSettingsSchema,
        signal,
      }),
    enabled,
  });
}

export function useUpdateReportingSettings(workspaceId: string) {
  return useWorkspaceMutation(workspaceId, (body: UpdateReportingSettingsRequest) =>
    apiPut(workspacePath(workspaceId, 'settings', 'reporting'), body, reportingSettingsSchema),
  );
}
